import { useEffect, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import CLENAddEditForm from "@/components/records/child/CLENAddEditForm";
import Popup from "@/components/shared/Popup";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { useLeaData } from "@/queries.ts";
import { CLEN_TEMPLATE, type CLEN } from "@/models.ts";
import { type CHLD, clenMutationFunctions } from "@/models/chld";
import { type ComboBoxOption } from "@/components/shared/forms/SearchComboBox";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

export type CLENEditDialogProps = {
  selectedCLEN: CLEN | undefined;
  clenDefaults?: Partial<CLEN>;
  child?: CHLD;
  onClose: () => void;
  viewOnly?: boolean;
  onDeleted?: (record: CLEN) => void;
  footerMode?: "default" | "back";
};

export default function CLENEditDialog({
  selectedCLEN,
  clenDefaults,
  child,
  onClose,
  viewOnly = false,
  onDeleted,
  footerMode = "default",
}: CLENEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const initialValues: CLEN =
    selectedCLEN ?? ({ ...CLEN_TEMPLATE, ...(clenDefaults ?? {}) } as CLEN);

  const addEditForm = useForm<CLEN>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedCLEN) {
      addEditForm.reset(selectedCLEN);
    } else {
      addEditForm.reset({ ...CLEN_TEMPLATE, ...(clenDefaults ?? {}) });
    }
    addEditForm.clearErrors();
  }, [selectedCLEN, clenDefaults, addEditForm]);

  const classroomOptions: ComboBoxOption[] =
    leaData?.entities?.classrooms && (child || selectedCLEN)
      ? Object.values(leaData.entities.classrooms)
          .map((c) => c.records[0])
          .filter((clrm) => {
            if (!clrm || !clrm.LocalClassroomId) return false;
            const ctx = child ?? selectedCLEN;
            if (!ctx) return false;
            return (
              clrm.PreschoolCode === ctx.PreschoolCode &&
              clrm.ProgramYear === ctx.ProgramYear &&
              !clrm.EffectiveEndDate
            );
          })
          .map((clrm) => ({
            value: clrm.LocalClassroomId,
            label: clrm.ClassroomName ?? "",
            sublabel: `ID: ${clrm.LocalClassroomId}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "CLEN",
      toastMessage:
        "Classroom enrollment record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "CLEN",
    toastMessage: "Classroom enrollment record submitted for deletion",
    successMessage: "Classroom enrollment record deleted successfully",
    onSuccess: () => {
      if (selectedCLEN) onDeleted?.(selectedCLEN);
      onClose();
    },
  });

  const mutateFns = clenMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = clenMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<CLEN> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedCLEN) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(selectedCLEN, true);
    }
  };

  const formId = "frmCLEN";
  const hasRole = leaContext.hasRole("CLENEdit");
  const canEdit = hasRole && !viewOnly;
  const isBack = footerMode === "back";

  const deleteButton =
    hasRole && selectedCLEN ? (
      <LoaderButton
        variant="danger"
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        loading={isDeleting && isDeleteSubmitting}
        loadingText="Deleting..."
        className="d-flex align-items-center"
      >
        <FiTrash2 className="me-1" />
        Delete
      </LoaderButton>
    ) : undefined;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm}
        header={
          selectedCLEN == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Add Classroom Enrollment Record"
                  : "View Classroom Enrollment Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Edit Classroom Enrollment Record"
                  : "View Classroom Enrollment Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          selectedCLEN ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isBack ? undefined : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={isBack ? undefined : onClose}
        backButtonText={
          isBack ? (
            <div className="d-flex align-items-center gap-2">
              <FiArrowLeft size={16} />
              <span>Back</span>
            </div>
          ) : undefined
        }
        onBackButtonClick={isBack ? onClose : undefined}
        canEdit={canEdit}
        additionalFooterButton={isBack ? deleteButton : undefined}
        leftFooterButton={isBack ? undefined : deleteButton}
      >
        <Form id={formId} onSubmit={addEditForm.handleSubmit(onSubmit)}>
          <CLENAddEditForm
            selectedCLEN={selectedCLEN}
            form={addEditForm}
            clenDefaults={clenDefaults}
            classroomOptions={classroomOptions}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Classroom enrollment record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
